import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Calendar, Heart, Star, ShieldCheck, Save, Upload, Award, Flame, Lock, Link as LinkIcon, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import { LevelBadge, PatreonBadge } from "@/components/UserBadge";
import { toast } from "sonner";

export default function Profile() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [likes, setLikes] = useState([]);
  const [watchLater, setWatchLater] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [banners, setBanners] = useState([]);
  const [tab, setTab] = useState("liked");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", avatar: "", banner_id: "" });
  const [patreonBusy, setPatreonBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || "", avatar: user.avatar || "", banner_id: user.banner_id || "" });
    api.get("/users/me/liked-videos").then((r) => setLikes(r.data)).catch(() => {});
    api.get("/users/me/watch-later").then((r) => setWatchLater(r.data)).catch(() => {});
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
    api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
  }, [user]);

  // Handle Patreon OAuth return
  useEffect(() => {
    const p = searchParams.get("patreon");
    if (!p) return;
    if (p === "success") toast.success("Patreon linked!");
    else if (p === "no-tier") toast.message("Patreon linked, but no active membership found.");
    else if (p === "error") toast.error("Patreon authorization was cancelled or failed.");
    else if (p === "state") toast.error("Session expired. Please try again.");
    else if (p === "token") toast.error("Could not connect to Patreon. Try again.");
    refresh();
    searchParams.delete("patreon");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, refresh]);

  const connectPatreon = async () => {
    setPatreonBusy(true);
    try {
      const { data } = await api.get("/patreon/connect");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPatreonBusy(false);
    }
  };
  const disconnectPatreon = async () => {
    if (!window.confirm("Disconnect Patreon? You'll lose your tier badge and ad-free browsing.")) return;
    setPatreonBusy(true);
    try {
      await api.post("/patreon/disconnect");
      toast.success("Patreon disconnected");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPatreonBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2MB"); return; }
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, avatar: r.result }));
    r.readAsDataURL(f);
  };
  const save = async () => {
    try {
      await api.put("/users/me", { name: form.name, avatar: form.avatar, banner_id: form.banner_id || "" });
      toast.success("Profile updated");
      setEditing(false);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) return null;
  const currentBanner = banners.find((b) => b.id === user.banner_id);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
      <div className="relative rounded-3xl overflow-hidden mb-8 border border-white/5 bg-[#0e0e0e]">
        <div className="h-40 sm:h-56 bg-gradient-to-r from-[#2a0509] to-[#0e0e0e]">
          {currentBanner && <img src={currentBanner.image_url} alt="" className="w-full h-full object-cover opacity-60" />}
        </div>
        <div className="px-6 sm:px-10 pb-6 -mt-14 relative">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#E50914] to-[#7a0309] flex items-center justify-center text-4xl font-black uppercase shadow-[0_0_30px_rgba(229,9,20,0.4)] border-4 border-[#0a0a0a] overflow-hidden">
              {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : (user.name || "?")[0]}
            </div>
            <button data-testid="profile-edit-btn" onClick={() => setEditing(!editing)} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-bold">
              {editing ? "Cancel" : "Edit Profile"}
            </button>
          </div>
          <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl font-black">{user.name}</h1>
                {user.role === "admin" && <span className="text-[10px] font-bold text-[#E50914] uppercase tracking-widest">Admin</span>}
                {user.patreon_tier && <PatreonBadge tier={user.patreon_tier} />}
              </div>
              <p className="text-zinc-400 text-sm">{user.email}</p>
              <div className="mt-3 flex items-center gap-4 flex-wrap">
                <LevelBadge level={user.level} progress={user.level_progress} />
                <span className="text-xs text-zinc-400 inline-flex items-center gap-1.5"><Award className="w-3 h-3 text-[#FF9F0A]" /> {user.xp || 0} XP</span>
                <span className="text-xs text-zinc-400 inline-flex items-center gap-1.5"><Flame className="w-3 h-3 text-[#FF9F0A]" /> {user.login_streak || 0}d streak</span>
                <span className="text-xs text-zinc-500 inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <div className="glass-strong rounded-2xl p-6 mb-8 space-y-4" data-testid="edit-form">
          <h3 className="font-display font-bold text-lg">Edit profile</h3>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 block mb-1.5">Display name</span>
            <input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 block mb-1.5">Avatar</span>
            <div className="flex gap-3 items-center">
              <input value={form.avatar?.startsWith("data:") ? "" : form.avatar} onChange={(e) => setForm({ ...form, avatar: e.target.value })} placeholder="URL or upload" className="flex-1 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
              <input type="file" accept="image/*" hidden ref={fileRef} onChange={onFile} />
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm font-bold whitespace-nowrap"><Upload className="w-4 h-4" /> Upload</button>
            </div>
            {form.avatar && <img src={form.avatar} alt="" className="mt-2 w-16 h-16 rounded-xl object-cover border border-white/10" />}
          </label>
          <div>
            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 block mb-1.5">Banner</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <button onClick={() => setForm({ ...form, banner_id: "" })} className={`aspect-video rounded-lg border ${!form.banner_id ? "border-[#E50914]" : "border-white/10"} bg-black/50 flex items-center justify-center text-xs font-bold text-zinc-400`}>No banner</button>
              {banners.map((b) => {
                const locked = user.level < b.min_level;
                return (
                  <button key={b.id} disabled={locked} onClick={() => setForm({ ...form, banner_id: b.id })}
                    className={`relative aspect-video rounded-lg overflow-hidden border ${form.banner_id === b.id ? "border-[#E50914]" : "border-white/10"} ${locked ? "opacity-50 cursor-not-allowed" : "hover:border-[#E50914]/50"}`}>
                    <img src={b.image_url} alt={b.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-1 left-1.5 right-1.5 flex items-center justify-between text-[10px] font-bold text-white">
                      <span className="truncate">{b.name}</span>
                      {locked ? <span className="flex items-center gap-0.5 text-[#FF9F0A]"><Lock className="w-2.5 h-2.5" /> Lv{b.min_level}</span> : <span className="text-[#32D74B]">Lv{b.min_level}+</span>}
                    </div>
                  </button>
                );
              })}
              {banners.length === 0 && <p className="text-xs text-zinc-500 col-span-full">No banners available yet. Admins can add them.</p>}
            </div>
          </div>
          <button data-testid="edit-save" onClick={save} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold text-sm">
            <Save className="w-4 h-4" /> Save changes
          </button>
        </div>
      )}

      <div className="glass rounded-2xl p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="patreon-card">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
            user.patreon_tier === "Super Savager" ? "bg-[#FF9F0A]/15 border-[#FF9F0A]/50 text-[#FF9F0A]" :
            user.patreon_tier === "Savager" ? "bg-[#E50914]/15 border-[#E50914]/50 text-[#E50914]" :
            "bg-white/5 border-white/10 text-zinc-400"
          }`}>
            <LinkIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-black text-lg flex items-center gap-2">
              Patreon {user.patreon_tier && <PatreonBadge tier={user.patreon_tier} />}
            </div>
            <p className="text-xs text-zinc-400 mt-0.5">
              {user.patreon_tier
                ? "Ad-free browsing is active. Membership is re-verified on every login."
                : "Link your Patreon to unlock your tier badge and remove ads."}
            </p>
          </div>
        </div>
        {user.patreon_connected ? (
          <button data-testid="patreon-disconnect" onClick={disconnectPatreon} disabled={patreonBusy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-bold disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Disconnect
          </button>
        ) : (
          <button data-testid="patreon-connect" onClick={connectPatreon} disabled={patreonBusy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white text-sm font-bold shadow-[0_0_20px_rgba(229,9,20,0.35)] disabled:opacity-50">
            <LinkIcon className="w-4 h-4" /> Connect Patreon
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/10">
        {[["liked", "Liked", Heart], ["watch", "Watch later", Star], ["routes", "Favorite routes", ShieldCheck]].map(([k, l, I]) => (
          <button key={k} data-testid={`prof-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 border-b-2 ${tab === k ? "border-[#E50914] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
            <I className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "liked" && (
        likes.length === 0 ? <p className="text-sm text-zinc-500">Nothing yet.</p> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {likes.map((v) => <VideoCard key={v.id} video={{ ...v, liked_by_user: true }} />)}
        </div>
      )}
      {tab === "watch" && (
        watchLater.length === 0 ? <p className="text-sm text-zinc-500">Add videos with the &quot;Watch later&quot; button.</p> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {watchLater.map((v) => <VideoCard key={v.id} video={v} />)}
        </div>
      )}
      {tab === "routes" && (
        (user.favorite_routes || []).length === 0 ? <p className="text-sm text-zinc-500">Star a route to add it here.</p> :
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {routes.filter((r) => user.favorite_routes.includes(r.id)).map((r) => (
            <Link key={r.id} to={`/routes/${r.id}`} className="group relative rounded-2xl overflow-hidden aspect-[264/507] border border-white/5 hover-glow">
              <img src={r.character_image} alt={r.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
              <div className="absolute bottom-0 p-3"><h3 className="font-display font-black text-lg">{r.name}</h3></div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
